<template>
  <div class="question"
       :id="`question-id-${$props.question.idString}`"
       :data-type="$props.question.type"
       v-if="show"
       ref="questionComponent"
  >

    <legend v-if="debug" style="color: #ff0000">
      <div>
        id: {{ $props.question.id }}
      </div>
      <div>
        mc_id: {{ $props.question.mcId }}
      </div>
    </legend>

    <legend v-if="showIds" style="color: #ff0000">
      <div>
        id: {{ $props.question.mcId }}
      </div>
    </legend>

    <div class="question-question" v-if="hasQuestionText" v-html="$props.question.question.get()" />

    <div class="question-component-wrapper" :data-has-prompt="hasPrompt">
      <multiple-choice-component ref="controlsComponent"
                                 v-if="$props.question.type === QuestionType.MultipleChoice"
                                 :submission="submission.data"
                                 :question="question"
                                 @change="onInput"
      />
      <question-slider-component ref="controlsComponent"
                                 v-if="$props.question.type === QuestionType.Slider"
                                 :submission="submission.data"
                                 :question="question"
                                 @change="onInput"
      />
      <multiple-selection-component ref="controlsComponent"
                                    v-if="$props.question.type === QuestionType.MultipleSelection"
                                    :submission="submission.data"
                                    :question="question"
                                    @change="onInput"
      />
      <rank-component ref="controlsComponent"
                                    v-if="$props.question.type === QuestionType.Rank"
                                    :submission="submission.data"
                                    :question="question"
                                    @hasChanged="onInput"
      />
      <multiple-selection-table-component ref="controlsComponent"
                                          v-if="$props.question.type === QuestionType.MultipleSelectionTable"
                                          :submission="submission.data"
                                          :question="question"
                                          @change="onInput"
      />
      <multiple-choice-table-component ref="controlsComponent"
                                          v-if="$props.question.type === QuestionType.MultipleChoiceTable"
                                          :submission="submission.data"
                                          :question="question"
                                          @change="onInput"
      />
      <text-entry-component ref="controlsComponent"
                            v-if="$props.question.type === QuestionType.TextEntry"
                            :submission="submission.data"
                            :question="question"
                            @input="onInput"
                            @checkboxInput="onInput"
      />
      <textarea-entry-component ref="controlsComponent"
                            v-if="$props.question.type === QuestionType.TextareaEntry"
                            :submission="submission.data"
                            :question="question"
                            @input="onInput"
      />
      <unsure-component v-if="$props.question.type === QuestionType.Unsure" />
      <unsure1-a3-component v-if="$props.question.type === QuestionType.Unsure1A3" />
      <complete-component
          v-if="$props.question.type === QuestionType.Complete"
          @scrollToNextQuestion="scrollToNextQuestion"
      />

      <div class="prompt-gutter">
        <Transition :name="`prompt-${promptDirection}`">
          <div class="prompt"
               :data-prompt-direction="promptDirection"
               @click="onPromptClick"
               v-if="hasPrompt && displayPrompt"
          >
            <div class="prompt-button">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                <path d="M10,16.3c-0.1,0-0.2,0-0.3-0.1c-0.1,0-0.2-0.1-0.3-0.2l-5.4-5.4c-0.2-0.2-0.2-0.4-0.2-0.6c0-0.2,0.1-0.4,0.3-0.6c0.2-0.2,0.4-0.2,0.6-0.2c0.2,0,0.4,0.1,0.6,0.2l4,4V4.2c0-0.2,0.1-0.4,0.3-0.6C9.6,3.4,9.8,3.3,10,3.3s0.4,0.1,0.6,0.3c0.2,0.2,0.3,0.4,0.3,0.6v9.1l4-4c0.2-0.2,0.4-0.2,0.6-0.2c0.2,0,0.4,0.1,0.6,0.2c0.2,0.2,0.2,0.4,0.2,0.6c0,0.2-0.1,0.4-0.2,0.6L10.6,16c-0.1,0.1-0.2,0.1-0.3,0.2C10.2,16.3,10.1,16.3,10,16.3z"/>
              </svg>
            </div>
            <div class="prompt-pulse"></div>
          </div>
        </Transition>
      </div>

    </div>

  </div>
</template>

<script setup lang="ts">
import QualityMentoringFormQuestion from '../../models/QualityMentoringFormQuestion';
import QualityMentoringForm from '../../models/QualityMentoringForm';
import TextEntryComponent from './TextEntryComponent.vue';
import TextareaEntryComponent from './TextareaEntryComponent.vue';
import MultipleChoiceComponent from './MultipleChoiceComponent.vue';
import QuestionDisplayPolicy from '../../QuestionDisplayPolicy';
import MultipleSelectionComponent from './MultipleSelectionComponent.vue';
import MultipleSelectionTableComponent from './MultipleSelectionTableComponent.vue';
import MultipleChoiceTableComponent from './MultipleChoiceTableComponent.vue';
import RankComponent from './RankComponent.vue';
import { ref, reactive, watch, getCurrentInstance, inject, onMounted, onUnmounted } from 'vue';
import Manager from '../../../../core/Manager';
import QuestionType from '../../models/QuestionType';
import CompleteComponent from './CompleteComponent.vue';
import UnsureComponent from './UnsureComponent.vue';
import Unsure1A3Component from './Unsure1A3Component.vue';
import QualityMentoringFormPage from '../../models/QualityMentoringFormPage';
import QuestionSliderComponent from './QuestionSliderComponent.vue';

const props = defineProps({
  question: QualityMentoringFormQuestion
})

const controlsComponent = ref();
const questionComponent = ref();
declare const DEBUG: any;
const debug = ref(DEBUG);

const form = QualityMentoringForm.getInstance();

const submission = reactive(Manager.getInstance().submission);

const instance = getCurrentInstance();

const hasQuestionText = !!(props.question.question);

const isActiveQuestion = ref(false);

const displayPrompt = ref(false);

const promptDirection = ref('down');

const page: QualityMentoringFormPage = inject('page');

const showIds = inject('showIds');

const { activeQuestionIdString, updateActiveQuestionIdString } = inject('activeQuestionIdString');

const hasPrompt = (() => {
  switch (props.question.type) {
    case QuestionType.Complete:
    case QuestionType.Unsure1A3:
    case QuestionType.Unsure:
    case QuestionType.Rank:
      return false;
    default:
      return true;
  }
})();

let lastSaveTimeout = null;

const onInput = function() {
  if(activeQuestionIdString.value !== props.question.idString) {
    updateActiveQuestionIdString(props.question.idString);
  }
  show.value = updateShow();
  const event = new Event('afterSubmissionUpdate');
  document.body.dispatchEvent(event);

  QualityMentoringForm.getInstance().updateMetrics(submission);

  setTimeout(() => {
    updatePrompt();
  }, 0);

  clearTimeout(lastSaveTimeout);
  lastSaveTimeout = setTimeout(async () => {
    await submission.save();
  }, 0);

  const completeBlockMessageEvent = new Event('updateCompleteMessage');
  document.body.dispatchEvent(completeBlockMessageEvent);
}

watch(activeQuestionIdString, () => {
  isActiveQuestion.value = props.question.idString === activeQuestionIdString.value;
  if(!isActiveQuestion.value && displayPrompt.value) {
    hidePrompt();
  }
});

const updateShow = function() {
  return (new QuestionDisplayPolicy)
    .setBranch("root")
    .setQuestion(props.question)
    .setStartingQuestion(1)
    .setSubmission(submission)
    .apply()
}

const show = ref(updateShow());
instance.emit('update-show', show);
if(!show.value) {
  if(submission.data.root[props.question.path]) {
    delete submission.data.root[props.question.path]
  }
}

const updatePrompt = () => {
  const event = new Event('beforeUpdatePrompt');
  document.body.dispatchEvent(event);

  if(!page.nextUnansweredQuestionIdString) {
    hidePrompt();
    return;
  }

  if (props.question.type === QuestionType.MultipleChoice) {
    if(!page.isLastAnsweredQuestion(props.question, submission)) {
      showPrompt();
    } else {
      if(isNextUnansweredQuestionAfterThisQuestion()) {
        scrollToNextQuestion();
      } else {
        showPrompt();
      }
    }
    return;
  }

  if(
      props.question.type === QuestionType.MultipleSelection
      || props.question.type === QuestionType.MultipleSelectionTable
      || props.question.type === QuestionType.MultipleChoiceTable
  ) {
    const values = submission.data.root[props.question.path];
    if(values) {
      if (Object.keys(values).map(key => values[key]).filter((value) => value).length) {
        showPrompt();
        return;
      }
    }
  }

  if(props.question.type === QuestionType.Slider) {
    if(submission.data.root[props.question.path]) {
      showPrompt();
      return;
    }
  }

  if(props.question.type === QuestionType.TextEntry || props.question.type === QuestionType.TextareaEntry) {
    const optionIds = props.question.options.map(key => key.value);
    const questionIds = submission.data.root;
    const existingOptionIds = Object.keys(questionIds).filter(optionId => optionIds.includes(optionId));
    if(existingOptionIds.map(key => submission.data.root[key]).filter((value) => value).length) {
      showPrompt();
      return;
    }
  }

  hidePrompt();
}

const showPrompt = () => {
  const isAfter = isNextUnansweredQuestionAfterThisQuestion();
  promptDirection.value = isAfter === false ? 'up' : 'down';
  displayPrompt.value = true;
}

const hidePrompt = () => {
  displayPrompt.value = false;
}

const onPromptClick = () => {
  if(displayPrompt.value) {
    scrollToNextQuestion();
    hidePrompt();
  }
}

const scrollToNextQuestion = () => {
  const nextQuestionEl: HTMLElement = document.querySelector(`.form-pages #question-id-${page.nextUnansweredQuestionIdString}`);
  if (nextQuestionEl) {
    window.scrollTo({ top: nextQuestionEl.offsetTop, left: 0, behavior: 'smooth' });
  }
}

const isNextUnansweredQuestionAfterThisQuestion = ():boolean|undefined => {
  const nextQuestionEl: HTMLElement = document.querySelector(`.form-pages #question-id-${page.nextUnansweredQuestionIdString}`);
  if (nextQuestionEl) {
    const nextQuestionTop = nextQuestionEl.offsetTop;
    const thisQuestionTop = questionComponent.value.offsetTop;
    return nextQuestionTop > thisQuestionTop;
  }
  return undefined;
}

const afterSubmissionUpdate = () => {
  show.value = updateShow();
  if (!show.value) {
    controlsComponent.value?.clear();
  }
  instance.emit('update-show', show);
}

onMounted(() => {
  document.body.addEventListener('afterSubmissionUpdate', afterSubmissionUpdate);
});

onUnmounted(() => {
  document.body.removeEventListener('afterSubmissionUpdate', afterSubmissionUpdate);
});

defineExpose({
  show
})

</script>
